import { useCallback, useEffect, useState } from "react";
import type {
  PlanInformation,
  SingleCoverageInformation,
} from "../../../../data-model/coverageInformation";

export interface UseEvCoverageLocalEditStateArgs {
  /** Postgres/EV single-tier snapshot; resets local saves when this identity changes. */
  singleCoverage: SingleCoverageInformation;
  /** Treatment sub-tab key; exiting edit avoids cross-tab draft confusion. */
  treatmentTabKey: string;
  /**
   * Optional mutator run on the working copy when edit mode starts (e.g. seed an
   * empty treatment tab so BV fields are editable when EV returned no PR rows).
   */
  prepareWorkingCopyForEdit?: (draft: SingleCoverageInformation) => void;
}

export interface UseEvCoverageLocalEditStateResult {
  /** Merged view: locally saved edits, then in-session working copy while editing, else server props. */
  displayCoverage: SingleCoverageInformation;
  isEditing: boolean;
  beginEdit: () => void;
  /** Discards the in-session working copy and exits edit mode without persisting. */
  cancelEdit: () => void;
  /**
   * Commits the working copy locally. When `beforeCommit` is provided, runs it first
   * (e.g. Postgres persist); rolls back edit mode if it throws.
   */
  saveEdit: (
    beforeCommit?: (
      snapshot: SingleCoverageInformation,
    ) => void | Promise<void> | string[] | Promise<string[]>,
  ) => Promise<string[] | void>;
  patchPlanInformation: (patch: Partial<PlanInformation>) => void;
  /**
   * Applies a synchronous mutator to a deep clone of the working (or base) coverage root.
   *
   * @param recipe - Mutator for deductible/OOP/misc fields on `draft`
   */
  applyCoverageEdit: (
    recipe: (draft: SingleCoverageInformation) => void,
  ) => void;
}

/**
 * Local edit/save for the EV coverage column (plan summary + financial SummaryCards).
 * Merges in-session edits into `displayCoverage`; callers may persist via `saveEdit(beforeCommit)`.
 * Resets when the tier snapshot or treatment tab changes.
 */
export function useEvCoverageLocalEditState(
  args: UseEvCoverageLocalEditStateArgs,
): UseEvCoverageLocalEditStateResult {
  const { singleCoverage, treatmentTabKey, prepareWorkingCopyForEdit } = args;

  const [savedOverride, setSavedOverride] =
    useState<SingleCoverageInformation | null>(null);
  const [workingCopy, setWorkingCopy] =
    useState<SingleCoverageInformation | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setSavedOverride(null);
    setWorkingCopy(null);
    setIsEditing(false);
  }, [singleCoverage]);

  useEffect(() => {
    setWorkingCopy(null);
    setIsEditing(false);
  }, [treatmentTabKey]);

  const baseCoverage = savedOverride ?? singleCoverage;

  const displayCoverage = isEditing && workingCopy ? workingCopy : baseCoverage;

  const beginEdit = useCallback(() => {
    const draft = structuredClone(baseCoverage);
    prepareWorkingCopyForEdit?.(draft);
    setWorkingCopy(draft);
    setIsEditing(true);
  }, [baseCoverage, prepareWorkingCopyForEdit]);

  const cancelEdit = useCallback(() => {
    setWorkingCopy(null);
    setIsEditing(false);
  }, []);

  const saveEdit = useCallback(
    async (
      beforeCommit?: (
        snapshot: SingleCoverageInformation,
      ) => void | Promise<void> | string[] | Promise<string[]>,
    ): Promise<string[] | void> => {
      if (!workingCopy) {
        setIsEditing(false);
        return;
      }
      const snapshot = structuredClone(workingCopy);
      try {
        const beforeCommitResult = beforeCommit
          ? await beforeCommit(snapshot)
          : undefined;
        setSavedOverride(snapshot);
        setWorkingCopy(null);
        setIsEditing(false);
        return beforeCommitResult;
      } catch (error) {
        // Keep edit mode and working copy so the user can retry after an API failure.
        throw error;
      }
    },
    [workingCopy],
  );

  const patchPlanInformation = useCallback(
    (patch: Partial<PlanInformation>) => {
      setWorkingCopy((prev) => {
        const start = structuredClone(prev ?? baseCoverage);
        start.planInformation = { ...start.planInformation, ...patch };
        return start;
      });
    },
    [baseCoverage],
  );

  const applyCoverageEdit = useCallback(
    (recipe: (draft: SingleCoverageInformation) => void) => {
      setWorkingCopy((prev) => {
        const start = structuredClone(prev ?? baseCoverage);
        recipe(start);
        return start;
      });
    },
    [baseCoverage],
  );

  return {
    displayCoverage,
    isEditing,
    beginEdit,
    cancelEdit,
    saveEdit,
    patchPlanInformation,
    applyCoverageEdit,
  };
}
